import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/data/EmptyState';
import { Badge } from '@/components/ui/badge';
import { useBlockedWorkers } from '@/hooks/useBlockedWorkers';

/** seconds → compact "2d 4h" / "3h 12m" / "45s". */
function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const d = Math.floor(totalSeconds / 86_400);
  const h = Math.floor((totalSeconds % 86_400) / 3_600);
  const m = Math.floor((totalSeconds % 3_600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// A Processing job older than this is almost certainly wedged, not working —
// flag it so an operator can see the blocker at a glance.
const STUCK_THRESHOLD_SECONDS = 60 * 60; // 1h

/**
 * Inner body for the "Blocked workers" card: pool utilization plus each
 * Processing job (the blocker) and how long it has held its worker. Rendered
 * inside a <Card> by the overview route.
 */
export function BlockedWorkers() {
  const q = useBlockedWorkers();

  if (q.isError) return <ErrorState error={q.error} />;
  if (q.isLoading) return <Skeleton className="h-32 w-full" />;

  const { processing, totalWorkers, busyWorkers } = q.data!;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Workers occupied</span>
        <span className="tabular-nums font-medium">
          {busyWorkers} / {totalWorkers || '—'}
        </span>
      </div>

      {processing.length === 0 ? (
        <div className="text-xs text-muted-foreground">No jobs currently processing — no workers blocked.</div>
      ) : (
        <ul className="space-y-2">
          {processing.map((job) => {
            const stuck = job.ageSeconds >= STUCK_THRESHOLD_SECONDS;
            return (
              <li key={job.id} className="flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0">
                  <div className="truncate font-medium">{job.jobType}</div>
                  <div className="truncate text-muted-foreground">job #{job.id}</div>
                </div>
                <Badge variant={stuck ? 'danger' : 'warning'}>{formatDuration(job.ageSeconds)}</Badge>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
